import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { CompletePreviewPayload } from '@/types/email';

export interface EmailPreviewResponse {
  subject: string;
  html: string;
  text?: string;
  sender_email: string;
  template_variables: Record<string, string>;
}

export function useEmailPreview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const getPreview = useCallback(async (
    payload: CompletePreviewPayload
  ): Promise<EmailPreviewResponse | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('preview-email', {
        body: payload
      });

      if (functionError) {
        throw functionError;
      }

      if (!data.success || !data.preview) {
        throw new Error(data.error || 'Failed to generate preview');
      }

      return data.preview;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate email preview';
      setError(errorMessage);
      toast({
        title: 'Preview Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return {
    getPreview,
    isLoading,
    error
  };
}